import React, { FC, useState } from 'react';
import { ParamsProps } from '../../../../../../common/components/ParamsProvider';
import { useDataProvider } from 'src/app/common/utils';
import { useDispatch, useSelector } from 'react-redux';
import { AlertType, appendAlertItem, AuthenticationState } from '@pruforce/common-adminweb-sdk';
import { useIntl } from 'react-intl';
import { PaginateList } from '../../../../../../common/types/common-types';
import PruFilter, { PruFilterItemType } from 'src/app/common/components/PruTable/PruFilter';
import BulkUploadStatusList, { BulkUploadStatusItem } from './components/BulkUploadStatusList';

import { BULK_UPLOAD_TYPE } from 'src/app/modules/pruwallet/types/bulk-upload-types';
import { BulkUploadListParam, fetchBulkUploadStatusList } from 'src/app/modules/pruwallet/network/bulkUploadCrud';
import { approveBulkOperation } from 'src/app/modules/pruwallet/network/bulkUpload';
import { RootState } from 'src/redux/store';

const initialState: BulkUploadListParam = {
  pageSize: 5,
  page: 1,
};
type BulkUploadStatusListingPageProps = {} & ParamsProps;
const paramsInitiator = (initialParams?: Record<string, string>): BulkUploadListParam => {
  return initialParams
    ? {
        fileName: initialParams.fileName ?? '',
        page: 1,
        pageSize: 5,
      }
    : initialState;
};

const BulkUploadStatusListingPage: FC<BulkUploadStatusListingPageProps> = ({ onChangeQueryParams }) => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();
  const [filterState, setFilterState] = useState<BulkUploadListParam>(paramsInitiator());
  const [bulkUploadStatusList, setBulkUploadStatusList] = useState<PaginateList<BulkUploadStatusItem>>();
  const { user } = useSelector<RootState, AuthenticationState>((state) => state.auth);

  const { isLoading, refreshData } = useDataProvider<PaginateList<BulkUploadStatusItem>>(
    //@ts-ignore
    async () => {
      try {
        if (onChangeQueryParams) {
          onChangeQueryParams(filterState);
        }
        return await fetchBulkUploadStatusList(filterState, dispatch);
      } catch (err) {
        throw err;
      }
    },
    setBulkUploadStatusList,
    false,
  );

  const onApprove = async (id: string, sourceService: string) => {
    try {
      const result = await approveBulkOperation(id, user?.username ?? '', sourceService, dispatch);
      setBulkUploadStatusList((prev) => {
        if (!prev?.docs) return prev;
        return {
          ...prev,
          docs: prev.docs.map((item: BulkUploadStatusItem) =>
            item.id === result.id ? { ...item, status: result?.status, approvedAt: result?.approvedAt } : item,
          ),
        };
      });
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: 'Bulk operation approved successfully',
          },
        ]),
      );
    } catch (err: any) {
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: 'Error',
            content: `${err?.message}`,
          },
        ]),
      );
    }
  };

  return (
    <>
      <PruFilter
        title={Translation(`wallet.bulkUpload.filter`)}
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'fileName',
            initialValue: filterState.fileName,
            displayName: Translation('wallet.bulkUpload.common.filename'),
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'operationType',
            initialValue: filterState.operationType,
            displayName: Translation('wallet.bulkUpload.common.bulkUploadType'),
            choices: [
              { displayName: '', value: '' },
              {
                displayName: Translation('title.wallet.bulkUpload.upload.awardPreload'),
                value: BULK_UPLOAD_TYPE.AWARD_PRELOAD,
              },
              {
                displayName: Translation('title.wallet.bulkUpload.upload.awardAdjustment'),
                value: BULK_UPLOAD_TYPE.AWARD_ADJUMENT,
              },
              {
                displayName: Translation('title.wallet.bulkUpload.upload.credit'),
                value: BULK_UPLOAD_TYPE.CREDIT,
              },
            ],
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'dateFrom',
            fieldTo: 'dateTo',
            initialDateFrom: filterState.dateFrom,
            initialDateTo: filterState.dateTo,
            displayName: Translation('wallet.bulkUpload.common.updatedAt'),
          },
        ]}
        onChangeFilter={(data) => {
          setFilterState({
            ...filterState,
            fileName: data.fileName?.trim() ?? '',
            operationType: data.operationType,
            dateFrom: data.dateFrom,
            dateTo: data.dateTo,
          });
        }}
        fetchData={refreshData}
      />
      <BulkUploadStatusList
        isLoading={isLoading}
        bulkUploadStatusList={bulkUploadStatusList}
        onRefresh={() => {
          refreshData();
        }}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({
            ...filterState,
            page,
            pageSize: rowsPerPage,
          });
          refreshData();
        }}
        onApprove={onApprove}
      />
    </>
  );
};

export default BulkUploadStatusListingPage;
